import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, LucideIcon } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import SearchBar from './SearchBar';
import TabSwitch from './TabSwitch';

interface PageHeaderProps {
  title?: string; // Made optional since we're not displaying it
  subtitle?: string;
  icon: LucideIcon;
  iconColor?: string;
  iconBgColor?: string;
  showLogout?: boolean;
  showBackButton?: boolean;
  backTo?: string;
  backText?: string;
  rightContent?: React.ReactNode;
  className?: string;
  variant?: 'default' | 'dashboard' | 'settings';
  // Search functionality
  searchTerm?: string;
  setSearchTerm?: (term: string) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;
  // Tab functionality
  activeTab?: 'active' | 'archived';
  setActiveTab?: (tab: 'active' | 'archived') => void;
  showTabs?: boolean;
}

export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(({
  subtitle,
  icon: Icon,
  iconColor = "text-blue-600",
  iconBgColor = "bg-blue-100 dark:bg-blue-900/20",
  showLogout = false,
  showBackButton = true,
  backTo = "/dashboard",
  backText = "Dashboard",
  rightContent,
  className = "",
  variant = 'default',
  // Search props
  searchTerm,
  setSearchTerm,
  searchPlaceholder = "Search...",
  showSearch = false,
  // Tab props
  activeTab,
  setActiveTab,
  showTabs = false
}, ref) => {
  const { logout } = useAuthStore();
  const internalRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => internalRef.current as HTMLDivElement);

  // Set CSS custom property for header height
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (internalRef.current) {
        const height = internalRef.current.offsetHeight;
        // Only update if the height is significantly different from the default (80px)
        // This prevents unnecessary updates and eliminates timing issues
        if (Math.abs(height - 80) > 5) {
          document.documentElement.style.setProperty('--header-height', `${height}px`);
        }
      }
    };

    // Use requestAnimationFrame to ensure the header is fully rendered
    requestAnimationFrame(() => {
      updateHeaderHeight();
    });
    
    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, [showSearch, showTabs]); // Re-run when search/tabs visibility changes

  // Responsive sizing based on variant
  const getSizingClasses = () => {
    switch (variant) {
      case 'dashboard':
        return {
          container: "py-3",
          iconContainer: "p-2",
          icon: "h-5 w-5",
          title: "text-xl",
          subtitle: "text-sm",
          backButton: "p-1.5",
          backIcon: "h-4 w-4",
          margin: "mr-3"
        };
      case 'settings':
        return {
          container: "py-2 sm:py-4",
          iconContainer: "p-2",
          icon: "h-5 w-5",
          title: "text-xl",
          subtitle: "text-sm",
          backButton: "p-1.5",
          backIcon: "h-4 w-4",
          margin: "mr-3"
        };
      default:
        return {
          container: "py-3",
          iconContainer: "p-2",
          icon: "h-5 w-5",
          title: "text-xl",
          subtitle: "text-sm",
          backButton: "p-1.5",
          backIcon: "h-4 w-4",
          margin: "mr-3"
        };
    }
  };

  const sizing = getSizingClasses();
  const hasExtras = showSearch || showTabs;

  return (
    <div 
      ref={internalRef}
      className={`bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-sm border-b border-gray-200/50 dark:border-gray-700/50 fixed top-0 right-0 z-40 absolute${className}`}
      style={{
        left: 'var(--sidebar-width, 0px)',
      }}
    >
      <div className={`w-full px-2 ${hasExtras ? sizing.container : 'h-16 py-3'}`}>
        {/* Main Header Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {showBackButton && (
              <Link
                to={backTo}
                className={`${sizing.margin} ${sizing.backButton} text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200`}
                title={`Back to ${backText}`}
              >
                <ArrowLeft className={`${sizing.backIcon}`} />
              </Link>
            )}
            <div className="flex items-center">
              <div className={`${sizing.iconContainer} ${iconBgColor} rounded-lg ${sizing.margin} flex items-center`}>
                <Icon className={`${sizing.icon} ${iconColor}`} />
              </div>
              {subtitle && (
                <div>
                  <p className={`${sizing.subtitle} text-gray-600 dark:text-gray-400`}>
                    {subtitle}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-3">
            {rightContent}
            {showLogout && (
              <button
                onClick={logout}
                className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
              >
                <span className="hidden sm:inline">Logout</span>
                <span className="sm:hidden">Exit</span>
              </button>
            )}
          </div>
        </div>

        {/* Search and Tabs Row */}
        {(showSearch || showTabs) && (
          <div className="mt-4 space-y-3">
            {/* Search Bar */}
            {showSearch && searchTerm !== undefined && setSearchTerm && (
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                placeholder={searchPlaceholder}
                className="w-full"
              />
            )}

            {/* Tab Switch */}
            {showTabs && activeTab !== undefined && setActiveTab && (
              <TabSwitch
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                className="w-full"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
});

PageHeader.displayName = 'PageHeader';

export default PageHeader;
